import { message } from "antd";
import { handleResponse } from "../../../common/handleLambdaResponse";
import { getIdToken } from "../../userManager/userManagementUtils";
import axios from "axios";
import { FileUploadItem } from "../../../types/interfaces";

export const preHashFile = async (file: File) => {
    return new Promise(async (resolve, reject) => {
        const [fileName, ext] = file.name.split(".");
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            reject(err);
        }
        const payload: any = {
            stage: "pre-hash",
            file_name: fileName,
            file_size: file.size,
            file_type: ext,
            token: idToken
        }
        const params = {
            FunctionName: "lamda-upload-requestor",
            Payload: JSON.stringify(payload)
        };
        window.lambda.invoke(params, (err, data) => {
            handleResponse(err, data, resolve, reject);
        });
    });
}

export const postHashFile = async (file: File, hash: string, ownerId: string,) => {
    return new Promise(async (resolve, reject) => {

        const [fileName, ext] = file.name.split(".");
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            reject(err);
        }
        const paramsTest = {
            FunctionName: "lamda-upload-requestor",
            Payload: JSON.stringify({
                stage: "post-hash",
                sha_hash: hash,
                file_name: fileName,
                file_size: file.size,
                file_type: ext,
                owner: ownerId,
                token: idToken
            })
        };

        window.lambda.invoke(paramsTest, async (err: any, resp: any) => {
            handleResponse(err, resp, resolve, reject);
        })
    });
}

export const handleMultipartUpload = async (data: any, file : File, updatePercentage: (percentage : number) => void, fileUpload : FileUploadItem): Promise<any[] | null> => {
    const etag: any[] = [];
    const perHashPercentage = 100 / data.signed_urls.length;
    const percentCompleted: number[] = Array(data.signed_urls.length).fill(0); // Initialize progress array

    const maxConcurrentUploads = 10;
    const retryDelay = 1000;
    const controllers: AbortController[] = [];

    const cancelAllUploads = () => {
        controllers.forEach(controller => controller.abort());
    };
    fileUpload.cancelCallback = cancelAllUploads;

    const uploadPart = async (signedUrl: string, start: number, end: number, i: number) => {
        let partRetry = 0;
        const controller = new AbortController();
        controllers.push(controller);

        while (true) {
            try {
                console.log("uploading part " + (i + 1));
                const promise = await axios.put(signedUrl, file.slice(start, end), {
                    headers: {
                        "Content-Type": null
                    },
                    signal: controller.signal,
                    onUploadProgress: (e: any) => {
                        percentCompleted[i] = (Math.round((e.loaded * 100) / e.total) * perHashPercentage) / 100;
                        const totalPercentage = percentCompleted.reduce((total, num) => {
                            return total + num;
                        }, 0);
                        updatePercentage(totalPercentage);
                        console.log(totalPercentage);
                    }
                });

                etag.push({ ETag: promise.headers.etag.replace(/"/g, ""), PartNumber: i + 1 });
                break;
            } catch (error) {
                if (axios.isCancel(error)) {
                    etag.push({ ETag: "CANCELLED", PartNumber: i + 1 });
                    break;
                }
                console.log(`Error on attempt ${partRetry + 1} for part ${i + 1}: ${error}`);
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
                partRetry++;
            }
        }
    };

    for (let batchStart = 0; batchStart < data.signed_urls.length; batchStart += maxConcurrentUploads) {
        const partBatch = data.signed_urls.slice(batchStart, batchStart + maxConcurrentUploads);

        const partPromises = partBatch.map((signedUrl : any, i : number) => {
            const start = (batchStart + i) * data.part_size;
            const end = Math.min(start + data.part_size, file.size);
            return uploadPart(signedUrl, start, end, batchStart + i);
        });

        await Promise.all(partPromises);
    }

    etag.sort((a, b) => a.PartNumber - b.PartNumber);
    
    // Check if any parts were cancelled
    if (etag.some((part) => part.ETag === "CANCELLED")) {
        return null;
    }

    return etag;
};

export const completeMultipartUpload = async (
    file: FileUploadItem,
    etags: any[]
) => {
    const [fileName, ext] = file.file.name.split(".");

   return new Promise(async (resolve, reject) => {
       let idToken;
       try {
           idToken = await getIdToken();
       } catch (err) {
           message.error("Error requesting upload.");
           return
       }
       const payload = {
           stage: "complete-upload",
           sha_hash: file.hash,
           file_name: fileName,
           file_size: file.file.size,
           file_type: ext,
           owner_id: file.ownerId,
           token: idToken,
           etags: etags
       }
       const paramsTest = {
           FunctionName: "lamda-upload-requestor",
           Payload: JSON.stringify(payload)
       };

       window.lambda.invoke(paramsTest, async (err: any, resp: any) => {
            handleResponse(err, resp, resolve, reject);
       });
    });
};

export const getUploadableOwners = async () => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            reject(err);
        }
        const payload: any = {
            stage: "get-uploadable-owners",
            token: idToken
        }
        const params = {
            FunctionName: "lamda-upload-requestor",
            Payload: JSON.stringify(payload)
        };
        window.lambda.invoke(params, (err, data) => {
            handleResponse(err, data, resolve, reject);
        });
    });
}

export const onSelectOwnerIdModalCancel = async (fileHash: string, selectedOwnerId: string) => {
    return new Promise(async (resolve, reject) => {
        let idToken;
        try {
            idToken = await getIdToken();
        } catch (err) {
            message.error("Error canceling upload.");
            return
        }
        const payload: any = {
            stage: "cancel",
            sha_hash: fileHash,
            owner: selectedOwnerId,
            token: idToken
        }
        const params = {
            FunctionName: "lamda-upload-requestor",
            Payload: JSON.stringify(payload)
        };
        window.lambda.invoke(params, (err, data) => {
            handleResponse(err, data, resolve, reject);
        });
    });
};